import { configureMfeSetup } from '@cw-elements/mfe-setup/config';
import { initializeFederation } from '@cw-elements/mfe-setup/federation-engine';
import { initCWElements } from '@cw-elements/config';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import { isLocal } from 'app.constant';
import { ArchivingProvider } from 'app/fields/archiving';
import { LimitCreationProvider } from 'app/fields/limit-creation';
import { MapProvider } from 'components/maps/map-context/map-context';
import 'cw-elements/lib/styles/index.less';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import * as ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { SENTRY_CONFIG, SENTRY_KEY } from 'sentry.config';
import 'services/async-requests/AsyncRequests';
import { request } from 'utils/axios';
import { getLocale } from 'utils/getDatePickerLocale';
import { AppLazy } from './app';
import { AppAuthProvider } from './auth/app-auth-provider';
import { ZendeskWidget } from './components/common/zendeskWidget';
import './components/maps/mapInit';
import './i18n';
import './icomoon-v1.0/style.css';
import './index.less';
import { store } from './redux/reducer/store';
import { CustomRouter } from './routes/customRouter';
import { getAccessToken as getAuthProviderAccessToken } from '@cw-elements/auth-provider';
import { history } from './utils/history';

if (!isLocal && SENTRY_KEY) {
  Sentry.init(SENTRY_CONFIG);
}

const initClients = () => {
  initCWElements({
    environment: process.env.REACT_APP_CW_ELEMENTS_ENVIRONMENT,
    axiosInstance: request,
    token: getAuthProviderAccessToken(),
    hostAppId: process.env.REACT_APP_BASE_REF_ID
  });
};

initClients();
const root = ReactDOM.createRoot(document.getElementById('root'));

const queryClient = new QueryClient();

const initializeApplication = () => {
  configureMfeSetup({ enableFederationRuntime: true });

  const remotesRegistered = initializeFederation([
    {
      name: 'entities-hall',
      entry: `${process.env.REACT_APP_CW_ELEMENTS_MFES}/entities-hall/mf-manifest.json`
    },
    {
      name: 'topbar-navigation',
      entry: `${process.env.REACT_APP_CW_ELEMENTS_MFES}/topbar-navigation/mf-manifest.json`
    },
    {
      name: 'inputs-mgt',
      entry: `${process.env.REACT_APP_CW_ELEMENTS_MFES}/inputs-mgt/mf-manifest.json`
    },
    {
      name: 'building-management',
      entry: `${process.env.REACT_APP_CW_ELEMENTS_MFES}/building-management/mf-manifest.json`
    },
    {
      name: 'quota-mgt',
      entry: `${process.env.REACT_APP_CW_ELEMENTS_MFES}/quota-mgt/mf-manifest.json`
    }
  ]);

  if (!remotesRegistered) {
    const error = new Error('Unable to register MFv2 remotes with the host React runtime.');
    Sentry.captureException(error);
    throw error;
  }

  root.render(
    <Provider store={store}>
      <ConfigProvider locale={getLocale()}>
        <QueryClientProvider client={queryClient}>
          <LDProvider clientSideID={process.env.REACT_APP_LAUNCHY_DARKLY_ID} timeout={5}>
            <CustomRouter history={history}>
              <AppAuthProvider>
                <LimitCreationProvider>
                  <ArchivingProvider>
                    <MapProvider>
                      <AppLazy />
                    </MapProvider>
                  </ArchivingProvider>
                </LimitCreationProvider>
              </AppAuthProvider>
            </CustomRouter>
          </LDProvider>
          <ZendeskWidget />
        </QueryClientProvider>
      </ConfigProvider>
    </Provider>
  );
};

initializeApplication();
